import React, { ReactElement, useEffect, useState } from 'react';
import { ToggleV2 } from 'swui';
import styled, { CSSObject } from '@emotion/styled';
import '../../../style/customSWui.css';
import SvgQuestion from '../../icons/SvgQuestion';
import { getLocalItem } from '../../../common';
import startCase from 'lodash/startCase';
import { handlePaymentVysionEvent } from '../../../utils/vysion/payment';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { PaymentViewVysionEventsEnum } from '../../../utils/vysion/payment/view';
import { getBackgroundTheme, getTextColor } from 'style';
import breakpoints from 'theme/material/breakpoints';
import { isDarkMode } from 'utils/helpers';

const AutoTopupContainer = styled('div')({
  display: 'flex',
  width: '100%',
});

const AutoTopupDetails = styled('div')({
  width: '100%',
  marginLeft: 0,
});

const AutoTopupActionWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const AutoTopupSentence = styled('p')(() => ({
  fontSize: 14,
  lineHeight: '19px',
  letterSpacing: -0.3,
  marginBottom: 0,
  ...getTextColor().LIGHT_GRAY,
  [breakpoints.down(769.05)]: {
    fontSize: 13,
  },
}));

const AutoTopupSentenceSpan = styled('span')(() => ({
  fontWeight: isDarkMode() ? 400 : 700,
}));

const AutoTopupNote = styled(AutoTopupSentence)(() => ({
  fontStyle: isDarkMode() ? 'normal' : 'italic',
}));

const AutoTopupTitle = styled('h5')(() => ({
  ...getTextColor().LIGHT_GRAY,
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: -0.3,
  margin: 0,
}));

const AutoTopupAction = styled('div')({
  flex: 'auto',
  marginRight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
});

const ToggleLabel = styled('span')<{ isOn: boolean }>(({ isOn }) => ({
  fontSize: 16,
  lineHeight: '21px',
  letterSpacing: -0.3,
  color: isDarkMode() ? (isOn ? '#E84A6E' : '#AFB0B1') : isOn ? '#E21D4A' : '#8F8F8F',
  transition: 'color 0.2s ease',
}));

const StyledToggle = styled(ToggleV2)({
  width: '40px !important',
  height: '20px !important',
});

const AutoTopupHelp = styled('div')(
  (): CSSObject => ({
    fontSize: 0,
    lineHeight: 0,
    display: 'inline-block',
    position: 'relative',
    ...getTextColor().LIGHT_GRAY,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      // color: getTextColor().LIGHT_GRAY,
    },
  })
);

const Popover = styled('div')<{ isDarkMode: boolean }>(
  (): CSSObject => ({
    width: 440,
    height: 'auto',
    position: 'absolute',
    left: '50%',
    top: '35px',
    transform: 'translateX(-50%)',
    zIndex: 1006,
    border: '1px solid #2b8fd7',
    borderRadius: 6,
    boxSizing: 'border-box',
    boxShadow: '0 1px 1px -1px rgba(0,0,0,.44)',
    display: 'flex',
    alignConent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    flexShrink: 0,
    ...getTextColor().LIGHT_GRAY,
    padding: 20,
    [breakpoints.down(769.05)]: {
      width: 315,
    },
    //top-hanging arrow
    '&:before, &:after': {
      border: 'solid transparent 12px',
      bottom: '100%',
      content: '" "',
      height: 0,
      pointerEvents: 'none',
      position: 'absolute',
      width: 0,
      borderBottomColor: '#2b8fd7',
    },

    '&:after': {
      borderBottomColor: '#fff',
      borderWidth: 12,
      top: -22,
    },
  }),
  ({ isDarkMode }) => ({
    background: getBackgroundTheme().block3,
    '&:before': {
      borderBottomColor: isDarkMode ? '#777' : '#2b8fd7',
    },
    '&:after': {
      borderBottomColor: getBackgroundTheme().block3,
    },
  })
);

const PopoverHeader = styled('h4')({
  color: '#E21D4A',
  fontSize: 18,
  fontWeight: 700,
  lineHeight: '23px',
  letterSpacing: '-0.36px',
  margin: 0,
  marginBottom: 10,
  textAlign: 'left',
  width: '100%',
  [breakpoints.down(769.05)]: {
    fontSize: 16,
    lineHeight: '19px',
    letterSpacing: '-0.32px',
  },
});

const PopoverText = styled('p')(() => ({
  margin: 0,
  fontSize: 14,
  fontWeight: 400,
  marginBottom: 10,
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  ...getTextColor().LIGHT_GRAY,
  [breakpoints.down(769.05)]: {
    fontSize: 13,
    lineHeight: '18px',
  },
}));

interface AutoTopupToggleProps {
  selectedPackage: any;
  onAutoTopupChange: any;
  packages: any;
  recurring: boolean;
  hideTopDescriptionInfo?: boolean;
}

const PopOverOverlay = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1005,
  backgroundColor: 'rgba(0,0,0,0.65)',
  overflow: 'auto',
});

const AutoTopupToggle = ({
  selectedPackage,
  onAutoTopupChange,
  recurring,
  packages,
  hideTopDescriptionInfo = false,
}: AutoTopupToggleProps): ReactElement => {
  const [showHelp, setShowHelp] = useState(false);

  const getRecurringPackages = () => {
    const lsBillingPackages = JSON.parse(getLocalItem('wyp_billing_packages')) ?? [];
    const availablePackages = packages ? packages : lsBillingPackages;

    const recurringPackages = availablePackages.filter(
      pack => typeof pack.with_recurring !== 'undefined' && pack.with_recurring === true
    );

    return recurringPackages;
  };

  const [recurringPackages] = useState(getRecurringPackages());

  const renderSelectpackage = () => {
    const isRecurrigPackage = recurringPackages.filter(
      recurringPackage => recurringPackage.identifier === selectedPackage?.identifier
    );

    if (isRecurrigPackage.length > 0) {
      return selectedPackage && startCase(selectedPackage.name.toLowerCase());
    }

    const defaultPackage = recurringPackages.filter(recurringPackage => recurringPackage.name == '100 CREDIT PACKAGE');

    return defaultPackage[0] && defaultPackage[0].name
      ? startCase(defaultPackage[0].name.toLowerCase())
      : '100 Credit Package';
  };

  useEffect(() => {
    if (showHelp) {
      handlePaymentVysionEvent({
        action: VYSION_ACTIONS.VIEW,
        event: PaymentViewVysionEventsEnum.VIEW_PAYMENT_NEVER_MISS_A_CONNECTION,
      });
    }
  }, [showHelp]);

  return (
    <AutoTopupContainer data-test-id="auto-topup-toggle-container">
      <AutoTopupDetails>
        <AutoTopupActionWrapper>
          <AutoTopupTitle>
            Auto Credit Top-Up{' '}
            <AutoTopupHelp
              auto-topup-toggle="auto-topup-toggle-help"
              onClick={() => {
                setShowHelp(!showHelp);
              }}
            >
              <SvgQuestion viewBox="0 0 512 500" styles={{ fill: '#2B8FD7', height: 18, width: 18 }} />
              {showHelp && (
                <div>
                  <PopOverOverlay
                    onClick={() => {
                      setShowHelp(false);
                    }}
                  />
                  <Popover
                    isDarkMode={false}
                    onClick={() => {
                      setShowHelp(false);
                    }}
                  >
                    <PopoverHeader>Never miss a connection!</PopoverHeader>
                    <PopoverText>
                      Auto Credit Top-Up ensures you&apos;re never out of the game by automatically refilling your
                      credits when you reach your low credit threshold.
                    </PopoverText>
                    <PopoverText>
                      You can change your top-up credit pack and low-credit threshold in Account Settings.
                    </PopoverText>
                  </Popover>
                </div>
              )}
            </AutoTopupHelp>
          </AutoTopupTitle>
          <AutoTopupAction>
            <ToggleLabel isOn={recurring}>{recurring ? 'On' : 'Off'}</ToggleLabel>
            <StyledToggle
              className="toggleSWUI"
              id="auto-topup-toggle"
              checked={recurring}
              onChange={onAutoTopupChange}
            />
          </AutoTopupAction>
        </AutoTopupActionWrapper>
        {recurring && !hideTopDescriptionInfo && (
          <div style={{ maxWidth: 430 }}>
            <AutoTopupSentence data-test-id="auto-topup-toggle-text">
              {renderSelectpackage()} will automatically repurchase when your credit balance gets below 25 credits.{' '}
              <AutoTopupSentenceSpan>You can adjust this in Account Settings.</AutoTopupSentenceSpan>
            </AutoTopupSentence>
            <AutoTopupNote data-test-id="auto-topup-toggle-note">
              Please note, you must have a saved payment card on file to enroll in Auto Credit Top-Up.
            </AutoTopupNote>
          </div>
        )}
      </AutoTopupDetails>
    </AutoTopupContainer>
  );
};

export default AutoTopupToggle;
